import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Button from '../../../elements/Button';
import { getMonth } from '../../../../helpers/localization/localizationUtils';
import Icon from '../../../elements/Icon';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';

function MonthNavigationCarousel(props) {
  const {
    onSelect,
    currentMonth: currentMonthDate,
    fromMonth,
    showShortMonths,
    previous,
    next,
    previousIcon,
    nextIcon,
    months,
    dateStyles,
    monthStyles,
    yearStyles,
    showYearBeforeMonth,
    showYear,
    showMonth
  } = props;
  const { direction, translate, localizationData, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const initialMonth = dayjs(fromMonth).startOf('month');
  const currentMonth = dayjs(currentMonthDate).startOf('month');
  const currentMonthIndex = currentMonth.diff(initialMonth, 'month');
  const month = getMonth(currentMonth, translate, localizationData, showShortMonths);
  const previousMonth = getMonth(
    currentMonth.subtract(1, 'month'),
    translate,
    localizationData,
    showShortMonths
  );
  const nextMonth = getMonth(
    currentMonth.add(1, 'month'),
    translate,
    localizationData,
    showShortMonths
  );
  const prevAriaLabel = previousMonth
    ? `${translate('cmp-fare-calendar-prev-month', 'Previous month')} ${previousMonth.name}`
    : null;
  const nextAriaLabel = nextMonth
    ? `${translate('cmp-fare-calendar-next-month', 'Next month')} ${nextMonth.name}`
    : null;

  function onNextClick() {
    const nextMon = dayjs(currentMonth).add(1, 'month');
    const item = getMonth(nextMon, translate, localizationData, showShortMonths);
    if (item) {
      onSelect(item);
    }
  }

  function onPreviousClick() {
    const prevMon = dayjs(currentMonth).subtract(1, 'month');
    const item = getMonth(prevMon, translate, localizationData, showShortMonths);
    if (item) {
      onSelect(item);
    }
  }

  return (
    <Box
      textAlign="center"
      display="flex"
      alignItems="center"
      rtlReverse
      className={className}
      style={style}
      dataTestId="MonthNavigationCarousel"
    >
      {currentMonthIndex !== 0 && (
        <Button
          {...previous}
          ariaLabel={prevAriaLabel}
          onClick={() => onPreviousClick()}
          dataTestId="PrevBtn"
        >
          <Icon icon="arrow-left" rotate {...previousIcon} />
        </Button>
      )}
      <Text {...dateStyles} flexGrow="1">
        {showYearBeforeMonth && showYear && (
          <Text type="span" {...yearStyles}>
            {month.year}
          </Text>
        )}
        {showMonth && (
          <Text type="span" {...monthStyles} marginWidth="2">
            {month.name}
          </Text>
        )}
        {!showYearBeforeMonth && showYear && (
          <Text type="span" {...yearStyles}>
            {month.year}
          </Text>
        )}
      </Text>
      {currentMonthIndex !== months - 1 && (
        <Button
          {...next}
          ariaLabel={nextAriaLabel}
          onClick={() => onNextClick()}
          dataTestId="NextBtn"
        >
          <Icon icon="arrow-right" rotate {...nextIcon} />
        </Button>
      )}

      <Box className="fr-clearfix" />
    </Box>
  );
  // };
}

MonthNavigationCarousel.propTypes = {
  /**
   * Months to show
   */
  months: PropTypes.number,
  /**
   * Function fire when change month
   */
  onSelect: PropTypes.func,
  /**
   * Current selected month
   */
  currentMonth: PropTypes.instanceOf(Date),
  /**
   * Styles to previous button
   */
  previous: PropTypes.objectOf(PropTypes.any),
  /**
   * Styles to next button
   */
  previousIcon: PropTypes.objectOf(PropTypes.any),
  /**
   * Styles to next button
   */
  next: PropTypes.objectOf(PropTypes.any),
  /**
   * Styles to next button
   */
  nextIcon: PropTypes.objectOf(PropTypes.any),
  /**
   * showShortMonths
   */
  showShortMonths: PropTypes.bool,
  /**
   * Styles to date content
   */
  dateStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Styles to month
   */
  monthStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Styles to year
   */
  yearStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Initial month
   */
  fromMonth: PropTypes.instanceOf(Date),
  /**
   * Render year before Month
   */
  showYearBeforeMonth: PropTypes.bool,
  /**
   * Show year atom
   */
  showYear: PropTypes.bool,
  /**
   * show months atom
   */
  showMonth: PropTypes.bool
};
MonthNavigationCarousel.defaultProps = {
  months: 3,
  onSelect: () => {},
  currentMonth: dayjs().toDate(),
  fromMonth: dayjs().toDate(),
  previous: {},
  previousIcon: {},
  next: {},
  nextIcon: {},
  dateStyles: {},
  monthStyles: {},
  yearStyles: {},
  showShortMonths: false,
  showYearBeforeMonth: false,
  showYear: true,
  showMonth: true
};

MonthNavigationCarousel.componentName = 'MonthNavigationCarousel';

export default MonthNavigationCarousel;
